import React from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "src/redux/hooks";
import { setItemPreview } from "src/redux/slices/items/itemSlice";

import { Image } from "@models/Image";
import { Item } from "@models/Item";
import { Variant } from "@models/Variant";
import { CldImage } from "@services/cloudinary";
import { ImageSize } from "@services/cloudinary/imageSizeOptions";
import { cn } from "@utils/tailwind";

import { getItemPreviewImage } from "../helpers";
import { itemsKeyFactory } from "../queries";

type ItemProps =
  | { item: Item; partialItem?: never }
  | { partialItem: { images: Image[]; id: string }; item?: never };

type ItemPreviewImageButtonProps = ItemProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    size?: ImageSize;
    variant?: Pick<Variant, "image" | "imageId"> | null;
    imageClassName?: string;
    hideOverlay?: boolean;
  };

const ItemPreviewImageButton = ({
  item,
  partialItem,
  variant,
  size,
  imageClassName,
  hideOverlay,
  className,
  ...props
}: ItemPreviewImageButtonProps) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const itemParts = item ?? partialItem;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (item) {
          queryClient.setQueryData<Item>(
            itemsKeyFactory.detail(item.id).queryKey,
            item
          );
        }
        dispatch(
          setItemPreview({
            id: itemParts.id,
            focusImageId: variant?.imageId ?? null,
          })
        );
      }}
      {...props}
      className={cn(
        "flex-shrink-0 w-20 relative rounded-md cursor-pointer overflow-hidden",
        !hideOverlay &&
          "after:absolute after:inset-0 after:bg-neutral-900 after:opacity-0 after:transition-opacity hover:after:opacity-20",
        className
      )}
    >
      <CldImage
        image={variant?.image ?? getItemPreviewImage(itemParts)}
        size={size ?? "small"}
        className={cn(
          "w-full aspect-square object-contain border-2 border-neutral-100 bg-white rounded-md",
          imageClassName
        )}
      />
    </button>
  );
};

export default ItemPreviewImageButton;
